import { NavbarSpacer, NavbarItem, NavbarLabel } from '@/components/catalyst-ui/navbar'
import { useActiveWorkspace } from '@/state/selection.store'
import { Button } from '@/components/catalyst-ui/button'
import { Dropdown, DropdownButton, DropdownItem, DropdownMenu } from '@/components/catalyst-ui/dropdown'
import { useLocation, useSearchParams, useNavigate } from 'react-router-dom'
import { CodeDesignBrandIcon } from '@/assets/icons'
import {
  UserCircleIcon,
  QuestionMarkCircleIcon,
  MegaphoneIcon,
  HomeModernIcon,
  ArrowRightStartOnRectangleIcon,
  ShieldCheckIcon,
  ChatBubbleLeftRightIcon,
  GiftIcon,
} from '@heroicons/react/20/solid'
import { openIntercomChat } from '@/lib/intercom'
import { openWhatsNewModal } from '@/components/WhatsNewModal/events'
import { useAuth } from '@/features/auth/hooks/useAuth'
import { useDraftGenerations } from '@/features/draft-generations/hooks/useDraftGenerations'
import { useUserProfile } from '@/features/profile/hooks/useUserProfile'
import { useAdminMode } from '@/hooks/useAdminMode'
import { getDisplayPlanName } from '@/lib/planDisplay'

const DRAFT_VISIBILITY_DAYS = 7

function DraftGenerationCountBadge({ count }) {
  if (!count) return null

  const label = count > 99 ? '99+' : String(count)
  const isCompact = label.length === 1

  return (
    <span
      aria-label={`${count} recent AI generation drafts`}
      className={[
        'absolute left-full top-0 z-10 -translate-y-1/2 rounded-[999px] bg-[#A16207]',
        'flex items-center justify-center text-[11px] font-semibold leading-[10px] text-white/80',
        'shadow-[0_2px_2px_rgba(0,0,0,0.1),0_4px_3px_rgba(0,0,0,0.1)]',
        isCompact ? 'h-[18px] w-[18px]' : 'h-[18px] min-w-[18px] px-1.5',
      ].join(' ')}
    >
      <span className="relative z-10 leading-[10px]">{label}</span>
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 rounded-[inherit] shadow-[inset_0_1px_0_rgba(255,255,255,0.14)]"
      />
    </span>
  )
}

export function WorkspaceNavbar() {
  const { workspaceId, workspace } = useActiveWorkspace()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { logout } = useAuth()
  const { profile } = useUserProfile()
  const { isAdminMode, targetUserId, targetUserLabel } = useAdminMode()
  const { counts, totalCount } = useDraftGenerations({
    lookbackDays: DRAFT_VISIBILITY_DAYS,
    limit: 20,
    mode: 'all',
  })

  const effectiveWorkspace = searchParams.get('workspace') || workspaceId || 'default'
  const draftGenerationCount = totalCount || (counts.aiCompanion || 0) + (counts.visualBuilder || 0)

  const navItems = [
    { to: `/?workspace=${effectiveWorkspace}`, label: 'Home' },
    { to: `/projects?workspace=${effectiveWorkspace}`, label: 'All Sites' },
    {
      to: `/draft-generations?workspace=${effectiveWorkspace}`,
      label: 'Recent AI Generations',
      draftCount: draftGenerationCount,
    },
    { to: `/ai?workspace=${effectiveWorkspace}`, label: 'Create Website with AI' },
    { to: `/templates?workspace=${effectiveWorkspace}`, label: 'Templates' },
    { to: `https://codedesign.ai/help`, label: 'Learn', external: true },
  ]

  const isActive = (path) => {
    if (path.startsWith('/?')) {
      return location.pathname === '/'
    }
    return location.pathname === path.split('?')[0]
  }

  const handleGoToManageWorkspaces = () => {
    navigate(`/settings/workspace-management?workspace=${encodeURIComponent(effectiveWorkspace)}`)
  }

  const handleGoToProfile = () => {
    navigate(`/profile?workspace=${encodeURIComponent(effectiveWorkspace)}`)
  }

  const handleSignOut = () => {
    logout()
  }

  const handleOpenHelpSupport = () => {
    window.open('https://docs.codedesign.ai', '_blank', 'noopener,noreferrer')
  }

  const handleOpenFeatureRequest = () => {
    window.open('https://codedesign.canny.io/feature-request', '_blank', 'noopener,noreferrer')
  }

  const handleOpenWhatsNew = () => {
    openWhatsNewModal({ source: 'workspace_navbar_profile_dropdown' })
  }

  return (
    <div className="h-[61px] bg-[#27272A] flex items-center w-full text-white">
      <button
        type="button"
        onClick={() => {
          const isHome = location.pathname === '/'
          if (!isHome) navigate(`/?workspace=${effectiveWorkspace}`)
        }}
        className="h-full w-14 hidden lg:flex items-center justify-center border-r border-[#3F3F46] cursor-pointer"
      >
        <CodeDesignBrandIcon className="w-8 h-8" />
      </button>

      <div className="hidden lg:flex items-center gap-3 px-4">
        {navItems.map((item) => (
          <div key={item.to} className="relative">
            {item.external ? (
              <button
                onClick={() => window.open(item.to, '_blank', 'noopener,noreferrer')}
                className="relative flex min-w-0 items-center gap-3 rounded-lg p-2 text-left text-base/6 font-medium sm:text-sm/5 cursor-pointer"
              >
                <span className="text-sm font-medium text-[#A1A1AA] truncate">
                  {item.label}
                </span>
              </button>
            ) : (
              <NavbarItem to={item.to}>
                {item.draftCount ? (
                  <span className={`relative inline-flex items-center text-sm font-medium ${isActive(item.to) ? 'text-white' : 'text-[#A1A1AA]'}`}>
                    <span>{item.label}</span>
                    <DraftGenerationCountBadge count={item.draftCount} />
                  </span>
                ) : (
                  <NavbarLabel
                    className={`text-sm font-medium ${isActive(item.to) ? 'text-white' : 'text-[#A1A1AA]'}`}
                  >
                    {item.label}
                  </NavbarLabel>
                )}
              </NavbarItem>
            )}
            {isActive(item.to) && !item.external && (
              <div className="absolute w-[40px] -bottom-1 left-1/2 transform -translate-x-1/2 h-0.5 bg-[#3F3F46]" />
            )}
          </div>
        ))}
      </div>

      <NavbarSpacer />

      {/* Admin mode indicator */}
      {isAdminMode && (
        <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-500/20 border border-amber-500/40 mr-2">
          <ShieldCheckIcon className="w-3.5 h-3.5 text-amber-400" />
          <span className="text-xs font-semibold text-amber-400 truncate max-w-[120px]">
            {targetUserLabel || targetUserId}
          </span>
        </div>
      )}

      <div className="flex items-center gap-3 pr-4">
        {(() => {
          const ws = workspace || {}
          const activePlans = ws.activePlans || (ws.plans || []).filter(p => p?.isActive)
          const activePlan = activePlans[0]
          const planName = getDisplayPlanName(activePlan)
          const totalCredits = ws.aiCredits?.totalCredits ?? 0
          if (planName) {
            return (
              <button
                onClick={() => navigate(effectiveWorkspace ? `/pricing?workspace=${encodeURIComponent(effectiveWorkspace)}&feature=workspace_navbar` : '/pricing?feature=workspace_navbar')}
                className="flex items-center justify-center gap-2 rounded-lg border border-[#3F3F46] bg-[#18181B] shadow-sm cursor-pointer hover:bg-[#27272A] transition-colors"
                style={{ padding: '6px 10px' }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="10" viewBox="0 0 14 10" fill="none">
                  <path fillRule="evenodd" clipRule="evenodd" d="M6.625 0.625L9.29167 4.625L12.625 1.95833L11.2917 8.625H1.95833L0.625 1.95833L3.95833 4.625L6.625 0.625Z" stroke="#A1A1AA" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span className="text-sm font-semibold leading-5 text-[#A1A1AA]" style={{ fontFamily: 'Inter, sans-serif' }}>
                  {planName}{totalCredits > 0 ? ` (${totalCredits} AI credit/mo)` : ''}
                </span>
              </button>
            )
          }
          return (
            <Button
              onClick={() => navigate(effectiveWorkspace ? `/pricing?workspace=${encodeURIComponent(effectiveWorkspace)}&feature=workspace_navbar` : '/pricing?feature=workspace_navbar')}
              className="!text-sm !font-semibold !shadow-[0px_2px_0px_0px_#FFFFFF26_inset] [--btn-bg:#3F3F46] [--btn-border:#3F3F46] data-hover:[--btn-bg:#52525B] data-focus:!ring-2 data-focus:!ring-indigo-500 data-focus:!ring-offset-2">
              Upgrade now
            </Button>
          )
        })()}

        <Dropdown>
          <DropdownButton
            as="button"
            className="bg-black sm:-mr-4 -mr-2 flex items-center justify-center w-8 h-8 rounded-full overflow-hidden cursor-pointer data-focus:!ring-2 data-focus:!ring-indigo-500 data-focus:!ring-offset-2"
          >
            {typeof profile?.avatar === 'string' && profile.avatar.startsWith('http') ? (
              <img
                src={profile.avatar}
                alt={profile.name || profile.email || 'User avatar'}
                className="w-full h-full object-cover"
              />
            ) : (
              <span className="text-xs font-medium text-white">
                {(profile?.name || profile?.email || 'P').charAt(0).toUpperCase()}
              </span>
            )}
          </DropdownButton>
          <DropdownMenu className="!p-0 !rounded-xl !overflow-hidden !w-[192px] !flex !flex-col">
            <div className="lg:hidden">
              <div className="py-2 px-1">
                {navItems.map((item) => (
                  <button
                    key={item.to}
                    className={`w-full px-3 text-left text-sm font-medium p-2 rounded-lg data-focus:!ring-2 data-focus:!ring-indigo-500 data-focus:!ring-offset-2 ${isActive(item.to)
                      ? 'bg-[#F4F4F5] text-[#A1A1AA]'
                      : 'text-[#18181B] hover:bg-zinc-950/5'
                      }`}
                    onClick={() => item.external ? window.open(item.to, '_blank', 'noopener,noreferrer') : navigate(item.to)}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
              <div className="border-t border-[#E4E4E7] mb-1 mx-1" />
            </div>

            {/* Bottom section: account actions */}
            <div className="py-0.5 sm:py-1 px-1 flex flex-col">
              <div className="flex flex-col -mt-2 lg:mt-0 gap-1 p-2 border-b-[1.5px] border-[#E4E4E7]">
                <div className="text-sm text-[#09090B] font-medium text-left px-1">
                  {profile?.name}
                </div>
                <div className="text-xs text-[#71717A] font-medium text-left px-1 truncate">
                  {profile?.email}
                </div>
              </div>
              <DropdownItem onClick={handleGoToProfile}>
                <div className="flex -my-0.5 sm:my-0 sm:py-0.5 items-center gap-3 text-sm text-[#09090B]">
                  <UserCircleIcon className="w-4 h-4 text-[#818182]" />
                  <span>My Profile</span>
                </div>
              </DropdownItem>
              <DropdownItem onClick={handleGoToManageWorkspaces}>
                <div className="flex -my-0.5 sm:my-0 sm:py-0.5 items-center gap-3 text-sm text-[#09090B]">
                  <HomeModernIcon className="w-4 h-4 text-[#818182]" />
                  <span>Workspaces</span>
                </div>
              </DropdownItem>
              <DropdownItem onClick={handleOpenHelpSupport}>
                <div className="flex -my-0.5 sm:my-0 sm:py-0.5 items-center gap-3 text-sm text-[#09090B]">
                  <QuestionMarkCircleIcon className="w-4 h-4 text-[#818182]" />
                  <span>Help Docs</span>
                </div>
              </DropdownItem>
              <DropdownItem onClick={() => openIntercomChat()}>
                <div className="flex -my-0.5 sm:my-0 sm:py-0.5 items-center gap-3 text-sm text-[#09090B]">
                  <ChatBubbleLeftRightIcon className="w-4 h-4 text-[#818182]" />
                  <span>Chat with Support</span>
                </div>
              </DropdownItem>
              <DropdownItem onClick={handleOpenFeatureRequest}>
                <div className="flex -my-0.5 sm:my-0 sm:py-0.5 items-center gap-3 text-sm text-[#09090B]">
                  <MegaphoneIcon className="w-4 h-4 text-[#818182]" />
                  <span>Feature Request</span>
                </div>
              </DropdownItem>
              <DropdownItem onClick={handleOpenWhatsNew}>
                <div className="flex -my-0.5 sm:my-0 sm:py-0.5 items-center gap-3 text-sm text-[#09090B]">
                  <GiftIcon className="w-4 h-4 text-[#818182]" />
                  <span>What&apos;s New</span>
                </div>
              </DropdownItem>
              <DropdownItem onClick={handleSignOut}>
                <div className="flex -my-0.5 sm:my-0 sm:py-0.5 items-center gap-3 text-sm text-[#09090B]">
                  <ArrowRightStartOnRectangleIcon className="w-4 h-4 text-[#818182]" />
                  <span>Sign out</span>
                </div>
              </DropdownItem>
            </div>
          </DropdownMenu>
        </Dropdown>
      </div>
    </div>
  )
}
